import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  CheckSquare, TrendingUp, CheckCircle, AlertTriangle, Calendar,
  LayoutGrid, Search, Pencil, Trash2, Play, Pause, Clock,
  Target, Zap, Users, Settings, List, LayoutGrid as GridIcon, Filter,
} from 'lucide-react';

import { StatusBadge } from '@/components/shared/StatusBadge';
import { PriorityBadge } from '@/components/shared/PriorityBadge';
import { TypeBadge } from '@/components/shared/TypeBadge';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/stores/auth.store';
import { dashboardService } from '@/services/dashboard.service';
import { taskService, type Task } from '@/services/task.service';
import { cn } from '@/lib/utils';

// Extended task type matching what the list API actually returns
interface DashboardTask extends Task {
  goal?: { id: string; title: string; reference_id: string } | null;
  sprint?: { id: string; name: string; reference_id: string } | null;
  column?: { id: string; name: string; display_order: number } | null;
  group?: { id: string; name: string; reference_id: string } | null;
  team?: { id: string; name: string; reference_id: string } | null;
  tags?: { id: string; name: string; color: string }[];
}

// ---------------------------------------------------------------------------
// Timer helper — live HH:MM:SS
// ---------------------------------------------------------------------------
function LiveTimer({ accumulatedSeconds, startedAt }: { accumulatedSeconds: number; startedAt: string | null }) {
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (startedAt) {
      const startMs = new Date(startedAt).getTime();
      function tick() {
        setElapsed(Math.floor((Date.now() - startMs) / 1000));
      }
      tick();
      intervalRef.current = setInterval(tick, 1000);
    } else {
      setElapsed(0);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [startedAt]);

  const total = accumulatedSeconds + (startedAt ? elapsed : 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const display = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;

  return <span className="font-mono text-xl font-semibold tabular-nums">{display}</span>;
}

// ---------------------------------------------------------------------------
// Stat card with colored left border
// ---------------------------------------------------------------------------
function BorderStatCard({
  icon: Icon, label, value, borderColor,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  borderColor: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-xs" style={{ borderLeft: `4px solid ${borderColor}` }}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-bold tracking-tight">{value}</p>
        </div>
        <div className="rounded-lg bg-muted p-2">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Task Card with embedded timer
// ---------------------------------------------------------------------------
function TaskCard({ task }: { task: DashboardTask }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const isRunning = !!task.timer_started_at;
  const isPaused = !isRunning && task.accumulated_seconds > 0;
  const isLate = task.calculated_status === 'late' || task.calculated_status === 'completed_late';

  const startMut = useMutation({
    mutationFn: () => taskService.timerStart(task.id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['dashboard-tasks'] }); },
    onError: (e: any) => toast({ title: 'Timer error', description: e.response?.data?.error?.message || 'Failed to start', variant: 'destructive' }),
  });
  const pauseMut = useMutation({
    mutationFn: () => taskService.timerPause(task.id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['dashboard-tasks'] }); },
    onError: (e: any) => toast({ title: 'Timer error', description: e.response?.data?.error?.message || 'Failed to pause', variant: 'destructive' }),
  });
  const finishMut = useMutation({
    mutationFn: () => taskService.updateStatus(task.id, 'done'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-badge'] });
      toast({ title: 'Task completed' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });
  const deleteMut = useMutation({
    mutationFn: () => taskService.delete(task.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-badge'] });
      toast({ title: 'Task deleted' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  // Parent entity
  const parentEntity = task.sprint
    ? { icon: Zap, name: task.sprint.name, color: 'text-violet-600' }
    : task.goal
    ? { icon: Target, name: task.goal.title, color: 'text-emerald-600' }
    : task.team
    ? { icon: Users, name: task.team?.name, color: 'text-blue-600' }
    : null;

  return (
    <div className={cn(
      'rounded-xl border bg-card shadow-xs overflow-hidden',
      isLate ? 'border-l-[3px] border-l-red-500' : 'border-l-[3px] border-l-teal-500',
    )}>
      <div className="p-4 space-y-2.5">
        {/* Row 1: badges */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-wrap">
            <TypeBadge type={task.task_type} />
            <span className="font-mono text-[11px] text-muted-foreground">{task.reference_id}</span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap shrink-0">
            <PriorityBadge priority={task.priority} />
            {isLate && (
              <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-400">Late</span>
            )}
            <StatusBadge status={task.phase_status} />
          </div>
        </div>

        {/* Row 2: title */}
        <Link to={`/tasks/${task.id}`} className="block text-sm font-semibold hover:text-brand-primary transition-colors line-clamp-1">
          {task.title}
        </Link>

        {/* Row 3: parent entity */}
        {parentEntity && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <parentEntity.icon className={cn('h-3 w-3', parentEntity.color)} />
            <span>{parentEntity.name}</span>
          </div>
        )}

        {/* Row 4: column badge */}
        {task.column && (
          <span className="inline-flex items-center rounded bg-violet-100 px-2 py-0.5 text-[10px] font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
            {task.column.name}
          </span>
        )}

        {/* Row 5: description */}
        {task.description && (
          <p className="text-xs text-muted-foreground line-clamp-2">{task.description}</p>
        )}

        {/* Row 6: assignee, dates, hours */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
          {task.assignee && (
            <div className="flex items-center gap-1.5">
              <UserAvatar name={task.assignee.full_name} avatarUrl={task.assignee.avatar_url} size="xs" />
              <span>{task.assignee.full_name}</span>
            </div>
          )}
          {(task.start_date || task.end_date) && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {task.start_date ? new Date(task.start_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
              {task.start_date && task.end_date ? ' — ' : ''}
              {task.end_date ? new Date(task.end_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
            </div>
          )}
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {Math.round(Number(task.actual_hours))}h / {task.estimated_hours != null ? `${task.estimated_hours}h` : '—'}
          </div>
        </div>

        {/* Row 7: tags */}
        {task.tags && task.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {task.tags.map((tag) => (
              <span key={tag.id} className="rounded px-1.5 py-0.5 text-[10px] font-medium" style={{ backgroundColor: tag.color + '20', color: tag.color }}>
                {tag.name}
              </span>
            ))}
          </div>
        )}

        {/* Row 8: action buttons */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="h-7 text-xs" asChild>
            <Link to={`/tasks/${task.id}`}><Pencil className="mr-1 h-3 w-3" /> Edit</Link>
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive" onClick={() => deleteMut.mutate()}>
            <Trash2 className="mr-1 h-3 w-3" /> Delete
          </Button>
        </div>
      </div>

      {/* Row 9: Timer section */}
      {task.phase_status !== 'done' && (
        <div className="border-t bg-muted/30 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <LiveTimer accumulatedSeconds={task.accumulated_seconds ?? 0} startedAt={task.timer_started_at} />
              {isRunning && (
                <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>
                  Running
                </span>
              )}
              {isPaused && (
                <span className="text-xs font-medium text-amber-600">Paused</span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground" aria-label="Timer settings">
                <Settings className="h-4 w-4" />
              </Button>
              {isRunning ? (
                <Button variant="outline" size="sm" className="h-8" onClick={() => pauseMut.mutate()}>
                  <Pause className="mr-1 h-3.5 w-3.5" /> Pause
                </Button>
              ) : (
                <Button variant="outline" size="sm" className="h-8" onClick={() => startMut.mutate()}>
                  <Play className="mr-1 h-3.5 w-3.5" /> {isPaused ? 'Resume' : 'Start'}
                </Button>
              )}
              <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => finishMut.mutate()}>
                <CheckCircle className="mr-1 h-3.5 w-3.5" /> Finish
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard Page
// ---------------------------------------------------------------------------
export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const [search, setSearch] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);

  // Stats
  const { data: dashData, isLoading: dashLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardService.getDashboard().then((r) => r.data.data),
  });

  // My tasks
  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['dashboard-tasks', showCompleted],
    queryFn: () => taskService.listMy({
      limit: 100,
      sort: 'created_at',
      order: 'desc',
      show_completed: showCompleted,
    }).then((r) => r.data),
  });

  const allTasks: DashboardTask[] = (tasksData?.data as DashboardTask[] | undefined) ?? [];
  const filteredTasks = search
    ? allTasks.filter((t) => t.title.toLowerCase().includes(search.toLowerCase()) || t.reference_id.toLowerCase().includes(search.toLowerCase()))
    : allTasks;

  const stats = dashData?.task_stats ?? { total: 0, not_started: 0, in_progress: 0, done: 0 };
  const overdueCount = dashData?.overdue_count ?? 0;
  const completionRate = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* 1. WELCOME BANNER */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-violet-600 via-purple-600 to-pink-500 px-6 py-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
              <LayoutGrid className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Welcome back, {user?.full_name ?? 'there'}!</h2>
              <p className="mt-0.5 text-sm text-white/70">Here's what's happening with your work today</p>
            </div>
          </div>
          <img src="/logo.png" alt="" className="h-12 w-12 opacity-80 hidden sm:block" />
        </div>
      </div>

      {/* 2. STAT CARDS */}
      {dashLoading ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          {Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <BorderStatCard icon={CheckSquare} label="Active Tasks" value={dashData?.my_active_tasks ?? 0} borderColor="#3B82F6" />
          <BorderStatCard icon={TrendingUp} label="In Progress" value={stats.in_progress} borderColor="#10B981" />
          <BorderStatCard icon={CheckCircle} label="Completed" value={stats.done} borderColor="#14B8A6" />
          <BorderStatCard icon={AlertTriangle} label="Overdue" value={overdueCount} borderColor="#EF4444" />
          <BorderStatCard icon={Calendar} label="Completion Rate" value={`${completionRate}%`} borderColor="#8B5CF6" />
        </div>
      )}

      {/* 3. TABS */}
      <Tabs defaultValue="my-tasks">
        <TabsList>
          <TabsTrigger value="my-tasks">My Tasks</TabsTrigger>
          <TabsTrigger value="workload">Workload Planner</TabsTrigger>
        </TabsList>

        {/* 4. MY TASKS TAB */}
        <TabsContent value="my-tasks" className="mt-4 space-y-4">
          {/* Toolbar */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search tasks..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox checked={showCompleted} onCheckedChange={(v) => setShowCompleted(v === true)} />
              Completed
            </label>
            <div className="flex items-center gap-1 rounded-md border p-0.5">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 bg-muted" aria-label="Card view">
                <GridIcon className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label="List view">
                <List className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="outline" size="sm" className="ml-auto">
              <Filter className="mr-1 h-4 w-4" /> Filters
            </Button>
          </div>

          {/* Task cards */}
          {tasksLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-48 rounded-xl" />)}
            </div>
          ) : filteredTasks.length === 0 ? (
            <EmptyState
              icon={CheckSquare}
              title={search ? 'No matching tasks' : 'No tasks assigned'}
              description={search ? 'Try a different search term.' : 'Tasks assigned to you will appear here.'}
            />
          ) : (
            <div className="space-y-4">
              {filteredTasks.map((task) => (
                <TaskCard key={task.id} task={task} />
              ))}
            </div>
          )}
        </TabsContent>

        {/* 5. WORKLOAD PLANNER TAB */}
        <TabsContent value="workload" className="mt-4">
          <EmptyState
            icon={Calendar}
            title="Coming soon"
            description="The workload planner will help you balance task distribution across sprints."
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
